import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Wand2, Loader2, X } from "lucide-react";
import { http, errMsg } from "../../lib/api";
import { FixCard } from "./FixCard";

const PartPicker = ({ kitId, page, onDone, onClose }) => {
  const [parts, setParts] = useState(null);
  const [block, setBlock] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setParts(null);
    http.get(`/kits/${kitId}/pdf/page/${page}/parts`).then((r) => { const p = r.data.parts || []; setParts(p); setBlock(p[0]?.block || ""); }).catch(() => setParts([]));
  }, [kitId, page]);
  const send = async () => {
    setBusy(true);
    try { await http.post(`/kits/${kitId}/page-fixes`, { page, block, instruction: text }); toast.success("Working on it — the fix will appear below for your approval"); setText(""); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (parts === null) return <div className="text-xs text-stone-400 py-3">Reading page {page + 1}…</div>;
  if (!parts.length) return <div data-testid="page-fix-no-parts" className="text-xs text-stone-500 py-3">Page {page + 1} is a fixed layout page (cover, contents, collection…) and can't be edited here.</div>;
  return (
    <div data-testid="page-fix-form" className="mt-3 space-y-3">
      <div className="flex items-center justify-between"><span className="text-[11px] uppercase tracking-[0.2em] text-stone-500">Fix page {page + 1}</span>
        <button data-testid="page-fix-close" onClick={onClose} className="text-stone-400 hover:text-stone-700"><X size={14} /></button></div>
      <select data-testid="page-fix-part-select" value={block} onChange={(e) => setBlock(e.target.value)} className="w-full rounded-sm border border-stone-300 px-2 py-2 text-sm bg-white">
        {parts.map((p) => <option key={p.block} value={p.block}>{p.label}{p.venue ? " (venue)" : ""}</option>)}
      </select>
      <textarea data-testid="page-fix-instruction" value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder='e.g. "The hours are wrong — it is closed on Mondays" or "Make the second paragraph less formal"'
        className="w-full rounded-sm border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:border-[#C9A847]" />
      {parts.find((p) => p.block === block)?.venue && <p className="text-[11px] text-stone-500">Any changed hours, address, price, phone or website will be re-checked on the live web before you approve.</p>}
      <button data-testid="page-fix-submit" disabled={busy || text.trim().length < 3 || !block} onClick={send}
        className="w-full inline-flex justify-center items-center gap-2 rounded-sm bg-[#2B2A26] px-4 py-2.5 text-sm font-semibold text-[#E9D48F] hover:bg-black disabled:opacity-40 transition-colors">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Prepare fix</button>
    </div>
  );
};

export const PageFixPanel = ({ kit, page, reload }) => {
  const [open, setOpen] = useState(false);
  const [fixes, setFixes] = useState([]);
  const load = useCallback(() => http.get(`/kits/${kit.id}/page-fixes`).then((r) => setFixes(r.data)).catch(() => {}), [kit.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!fixes.some((f) => f.status === "working")) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [fixes, load]);
  const done = () => { load(); reload(); };
  const busy = ["queued", "generating", "verifying", "fact_checking", "fixing"].includes(kit.status) || kit.filling;
  return (
    <aside data-testid="page-fix-panel" className="rounded-lg border border-stone-200 bg-[#FBFAF7] p-4">
      <div className="text-[11px] uppercase tracking-[0.3em] text-[#9A7B2C]">Admin only</div>
      <h3 className="font-display text-xl text-[#2B2A26] mt-1">Fix this page</h3>
      <p className="text-xs text-stone-500 mt-1">Pick the part of the page, say what's wrong, and approve the rewrite before it's saved.</p>
      {!open && <button data-testid="page-fix-open" disabled={busy} onClick={() => setOpen(true)} className="mt-3 w-full inline-flex justify-center items-center gap-2 rounded-sm border border-[#C9A847] px-4 py-2 text-sm font-semibold text-[#9A7B2C] hover:bg-[#FBF6E9] disabled:opacity-40 transition-colors">
        <Wand2 size={14} /> Fix page {page + 1}</button>}
      {busy && <p data-testid="page-fix-busy" className="mt-2 text-[11px] text-amber-600">The kit is busy — fixes unlock when it finishes.</p>}
      {open && !busy && <PartPicker kitId={kit.id} page={page} onDone={() => { setOpen(false); load(); }} onClose={() => setOpen(false)} />}
      <div className="mt-4 space-y-3">{fixes.map((f) => <FixCard key={f.id} f={f} kitId={kit.id} onDone={done} />)}</div>
    </aside>
  );
};
